import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import type { SpecificationDocument } from '../../../../../types';
import type { WeldJoint } from '../../SpoolsProduction';
import {
  actualPreheatMeetsWpsLimits,
  getWpsPreheatLimitsFromSpecifications,
} from '../weld/wpsPreheatLimits';
import { Button } from '../../../../../../../components/ui/button';
import { Input } from '../../../../../../../components/ui/input';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import { SearchableSelect } from '../../../../../../../components/ui/SearchableSelect';
import { WELD_PREHEAT_MEASUREMENT_METHOD_TAGS, type DocumentTag } from '../../../../../types';
import {
  FieldGroup,
  FormGrid,
  StepInfoBanner,
  TriStateCheckRow,
  type TriStateCheck,
} from '../weld/spoolDetailFormUi';
import { parseOptionalFloat, triStateToApi, type SpoolStepPanelContext } from '../weld/spoolStepPayloadHelpers';
import { apiTriToUi, getWeldStepPanelPayload, weldPanelsRevision } from '../weld/weldStepPanelHydration';
import { Card } from '../../../../../../../components/ui/card';
import { CardContent } from '../../../../../../../components/ui/card';

export type PreheatSubmissionMode = 'draft' | 'confirm_weld';

export interface PreheatSavePayload {
  mode: PreheatSubmissionMode;
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  wpsPreheatMinC: string;
  wpsPreheatMinCNum: number | null;
  wpsPreheatMaxC: string;
  wpsPreheatMaxCNum: number | null;
  actualPreheatTempC: string;
  actualPreheatTempCNum: number | null;
  measurementMethod: string;
  measuredBy: string;
  wpsMeetsPreheat: 'pass' | 'fail' | 'pending';
}

const DEFAULT_MEASUREMENT_METHOD_LABELS = ['Contact Thermometer', 'Temp Stick', 'Infrared'] as const;

/** Map legacy slugs / stored labels to display text; payload saves the label string only. */
function normalizeMeasurementMethodDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return 'Contact Thermometer';
  const s = String(raw).trim();
  const lower = s.toLowerCase().replace(/\s+/g, '_');
  if (lower === 'contact') return 'Contact Thermometer';
  if (lower === 'stick') return 'Temp Stick';
  if (lower === 'ir' || lower === 'infrared') return 'Infrared';
  if (s === 'Contact Thermometer' || s === 'Temp Stick' || s === 'Infrared') return s;
  return s;
}

export interface PreheatPanelProps {
  weld?: WeldJoint | null;
  context?: SpoolStepPanelContext;
  /** Pipe WPS documents — used to read `details.preheatTemperature` min/max (°C). */
  specifications?: SpecificationDocument[];
  onPreheatSave?: (payload: PreheatSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  onOpenRaiseNcr?: () => void;
  repairNo?: number | null;
}

export function PreheatPanel({
  weld,
  context,
  specifications = [],
  onPreheatSave,
  onSubmittedNcrClick,
  onOpenRaiseNcr,
  repairNo = null,
}: PreheatPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, {
        matchStepType: 'preheat',
      }),
    [ncrListAll, weld?.id, context?.weldId]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const wpsPreheatFromSpec = useMemo(
    () => getWpsPreheatLimitsFromSpecifications(specifications),
    [specifications]
  );

  const [wpsPreheatMinC, setWpsPreheatMinC] = useState('');
  const [wpsPreheatMaxC, setWpsPreheatMaxC] = useState('');
  const [actualPreheatTempC, setActualPreheatTempC] = useState('');
  const [measurementMethod, setMeasurementMethod] = useState('Contact Thermometer');
  const [measurementMethodTagsFromApi, setMeasurementMethodTagsFromApi] = useState<DocumentTag[]>([]);
  const [isAddingMeasurementMethod, setIsAddingMeasurementMethod] = useState(false);
  const [measuredBy, setMeasuredBy] = useState('sellaraja');
  const [wpsMeets, setWpsMeets] = useState<TriStateCheck>('neutral');
  const [activeSubmit, setActiveSubmit] = useState<PreheatSubmissionMode | null>(null);
  const activeSubmitRef = useRef<PreheatSubmissionMode | null>(null);

  const preheatRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'preheat')
  );

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  const measurementMethodSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_MEASUREMENT_METHOD_LABELS) {
      map.set(name, { id: name, name });
    }
    const pid = context?.projectId;
    for (const row of measurementMethodTagsFromApi) {
      const name = row.tags_name?.trim();
      if (!name) continue;
      if (pid != null && pid !== '' && row.project_id != null && String(row.project_id) !== String(pid)) {
        continue;
      }
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    if (measurementMethod && !map.has(measurementMethod)) {
      map.set(measurementMethod, { id: measurementMethod, name: measurementMethod });
    }
    return Array.from(map.values());
  }, [measurementMethodTagsFromApi, measurementMethod, context?.projectId]);

  useEffect(() => {
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      setMeasurementMethodTagsFromApi([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const tags = await getListMaterialCategory(WELD_PREHEAT_MEASUREMENT_METHOD_TAGS);
      if (cancelled || !Array.isArray(tags)) return;
      const forProject = tags.filter((t: DocumentTag) => String(t.project_id) === String(pid));
      setMeasurementMethodTagsFromApi(forProject);
    })();
    return () => {
      cancelled = true;
    };
  }, [context?.projectId]);

  const handleAddMeasurementMethod = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.warning('Project required', 'Select a project context before adding a measurement method.');
      setIsAddingMeasurementMethod(false);
      return;
    }
    if (measurementMethodSelectOptions.some((o) => o.name.toLowerCase() === v.toLowerCase())) {
      Notification.warning(`${v} already exists`, 'Choose another name or select it from the list.');
      setIsAddingMeasurementMethod(false);
      return;
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}') as { email?: string };
    const res = await AddMaterialCategory(
      {
        tags_name: v,
        project_id: pid,
        tags_type: WELD_PREHEAT_MEASUREMENT_METHOD_TAGS,
        created_by: user.email || 'admin',
      },
      'Measurement method'
    );
    if (res) {
      setMeasurementMethodTagsFromApi((prev) => {
        const row = res as DocumentTag;
        if (prev.some((p) => p.tags_id === row.tags_id)) return prev;
        return [...prev, row];
      });
      setMeasurementMethod(v);
    }
    setIsAddingMeasurementMethod(false);
  };

  useEffect(() => {
    if (wpsPreheatFromSpec) {
      setWpsPreheatMinC(wpsPreheatFromSpec.minC);
      setWpsPreheatMaxC(wpsPreheatFromSpec.maxC);
    }
  }, [wpsPreheatFromSpec]);

  useEffect(() => {
    const pl = getWeldStepPanelPayload(weld ?? null, 'preheat');
    if (!pl || Object.keys(pl).length === 0) return;
    if (pl.wpsPreheatMinC != null) setWpsPreheatMinC(String(pl.wpsPreheatMinC));
    if (pl.wpsPreheatMaxC != null) setWpsPreheatMaxC(String(pl.wpsPreheatMaxC));
    if (pl.actualPreheatTempC != null) setActualPreheatTempC(String(pl.actualPreheatTempC));
    if (pl.measurementMethod != null) {
      const display = normalizeMeasurementMethodDisplay(String(pl.measurementMethod));
      setMeasurementMethod(display);
    }
    if (pl.measuredBy != null) setMeasuredBy(String(pl.measuredBy));
    if (pl.wpsMeetsPreheat != null) setWpsMeets(apiTriToUi(pl.wpsMeetsPreheat));
  }, [weld?.id, weldPanelsRevision(weld ?? null)]);

  useEffect(() => {
    const actualNum = parseOptionalFloat(actualPreheatTempC);
    if (actualNum == null) return;
    const verdict = actualPreheatMeetsWpsLimits(actualNum, wpsPreheatFromSpec);
    if (verdict !== 'neutral') setWpsMeets(verdict);
  }, [actualPreheatTempC, wpsPreheatFromSpec]);

  const cycleWpsMeets = () => {
    setWpsMeets((cur) =>
      cur === 'neutral' ? 'pass' : cur === 'pass' ? 'fail' : 'neutral'
    );
  };

  const buildPayload = useCallback(
    (mode: PreheatSubmissionMode): PreheatSavePayload => ({
      mode,
      projectId: context?.projectId ?? null,
      pipeId: context?.pipeId ?? null,
      spoolId: context?.spoolId ?? null,
      spoolName: context?.spoolName ?? null,
      weldId: context?.weldId ?? null,
      weldNumber: context?.weldNumber ?? null,
      wpsPreheatMinC,
      wpsPreheatMinCNum: parseOptionalFloat(wpsPreheatMinC),
      wpsPreheatMaxC,
      wpsPreheatMaxCNum: parseOptionalFloat(wpsPreheatMaxC),
      actualPreheatTempC,
      actualPreheatTempCNum: parseOptionalFloat(actualPreheatTempC),
      measurementMethod,
      measuredBy,
      wpsMeetsPreheat: triStateToApi(wpsMeets),
    }),
    [
      context?.projectId,
      context?.pipeId,
      context?.spoolId,
      context?.spoolName,
      context?.weldId,
      context?.weldNumber,
      wpsPreheatMinC,
      wpsPreheatMaxC,
      actualPreheatTempC,
      wpsPreheatFromSpec,
      measurementMethod,
      measuredBy,
      wpsMeets,
    ]
  );

  const submit = (mode: PreheatSubmissionMode) => {
    if (!onPreheatSave) return;
    const payload = buildPayload(mode);
    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    onPreheatSave(payload);
  };

  useEffect(() => {
    if (!preheatRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [preheatRemoteSaving]);

  const handleConfirmAndProceedToWeld = () => void submit('confirm_weld');
  const handleSaveDraft = () => void submit('draft');
  const hasExistingPreheat = useMemo(() => {
    const pl = getWeldStepPanelPayload(weld ?? null, 'preheat');
    return !!pl && Object.keys(pl).length > 0;
  }, [weld?.id, weldPanelsRevision(weld ?? null)]);

  const actionText = hasExistingPreheat ? 'Update & Proceed to Weld' : 'Save & Proceed to Weld';
  const confirmLoadingText = hasExistingPreheat ? 'Updating…' : 'Saving…';
  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = preheatRemoteSaving && kind === 'draft';
  const savingConfirm = preheatRemoteSaving && kind === 'confirm_weld';
  /** Saga uses `context.weldId`; do not require `weld` object to be set for buttons to work. */
  const canAct = Boolean(weld?.id ?? context?.weldId);
  const draftDisabled = !canAct || savingConfirm;
  const confirmDisabled = !canAct || savingDraft;

  return (
    <div className="grid min-h-0 w-full grid-cols-1 gap-4">
      <Card className="flex h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
      {repairNo != null && !Number.isNaN(Number(repairNo)) ? (
        <StepInfoBanner title="Repair preheat" variant="notice">
          Repair cycle #{Number(repairNo)} — record preheat verification again.
        </StepInfoBanner>
      ) : null}
      {/* {ncrList.length > 0 &&
      onSubmittedNcrClick &&
      context?.projectId != null &&
      context?.projectId !== '' &&
      context?.pipeId != null &&
      context?.pipeId !== '' ? (
        <WorkflowNcrSummaryTable
          ncrList={ncrList}
          loading={ncrListLoading}
          step="preheat"
          stepTitle="Preheat"
          weldId={weld?.id ?? null}
          onRowClick={onSubmittedNcrClick}
        />
      ) : null} */}
      <FormGrid>
        <FieldGroup label="WPS Preheat Min (°C)">
          <Input
            className="h-9 text-sm bg-gray-50"
            value={wpsPreheatMinC}
            readOnly
            placeholder={wpsPreheatFromSpec ? undefined : 'Set on WPS specification'}
          />
        </FieldGroup>
        <FieldGroup label="WPS Preheat Max (°C)">
          <Input
            className="h-9 text-sm bg-gray-50"
            value={wpsPreheatMaxC}
            readOnly
            placeholder={wpsPreheatFromSpec ? undefined : 'Set on WPS specification'}
          />
        </FieldGroup>
        <FieldGroup label="Actual Preheat Temp (°C)" required>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 115"
            value={actualPreheatTempC}
            onChange={(e) => setActualPreheatTempC(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Measurement Method">
          <SearchableSelect
            value={measurementMethod}
            onValueChange={setMeasurementMethod}
            placeholder="Select measurement method"
            searchPlaceholder="Search methods…"
            options={measurementMethodSelectOptions}
            onAddNew={() => setIsAddingMeasurementMethod(true)}
            addNewText="Add measurement method"
            className="w-full [&_button]:h-9 [&_button]:text-sm"
          />
        </FieldGroup>
        {isAddingMeasurementMethod && (
          <AddNewItemDialog
            open={isAddingMeasurementMethod}
            onOpenChange={setIsAddingMeasurementMethod}
            title="Add measurement method"
            label="Measurement method"
            placeholder="e.g. Digital pyrometer"
            onAdd={handleAddMeasurementMethod}
            buttonText="Add"
            cancelText="Cancel"
          />
        )}
        <FieldGroup label="Measured By">
          <Input
            className="h-9 text-sm"
            placeholder="Name or ID"
            value={measuredBy}
            onChange={(e) => setMeasuredBy(e.target.value)}
          />
        </FieldGroup>
      </FormGrid>
      <div className="space-y-2 mt-2">
        <TriStateCheckRow
          tag="Preheat"
          labels={{
            neutral: 'Preheat vs WPS range — pending',
            pass: 'Preheat temperature within WPS min/max',
            fail: 'Preheat temperature outside WPS min/max',
          }}
          state={wpsMeets}
          onCycle={cycleWpsMeets}
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        {/* <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!canAct || savingDraft || savingConfirm}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button> */}
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={draftDisabled}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={handleSaveDraft}
        >
          Save Draft
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-hero-red text-white hover:bg-hero-red-60"
          disabled={confirmDisabled}
          loading={savingConfirm}
          loadingText={confirmLoadingText}
          onClick={handleConfirmAndProceedToWeld}
        >
          {actionText}
        </Button>
      </div>
      </CardContent>
    </Card>
    </div>
  );
}
